import { getRandomInt } from '../utils/randomUtils';
import type { BattleState, BattleMoveType } from '../types/battle';

// AI personality types and their move preferences
const STRATEGIES = {
  aggressive: {
    attack: 0.6,
    defend: 0.2,
    special: 0.2
  },
  balanced: {
    attack: 0.4,
    defend: 0.3,
    special: 0.3
  },
  defensive: {
    attack: 0.2,
    defend: 0.6,
    special: 0.2
  }
} as const;

export class AiStrategy {
  calculateNextMove(state: BattleState): { type: BattleMoveType; power: number } {
    // Get AI's strategy based on health state
    const strategy = this.determineStrategy(state);
    
    // Analyze battle situation
    const situation = this.analyzeSituation(state);
    
    // Choose move based on strategy and situation
    const moveType = this.chooseMoveType(strategy, situation, state);
    
    // Calculate power based on move type
    const power = this.calculatePower(moveType);

    return { type: moveType, power };
  }

  private determineStrategy(state: BattleState) {
    const healthPercentage = state.aiHealth / 100;

    // Adjust strategy based on health
    if (healthPercentage < 0.3) {
      return STRATEGIES.defensive; // Prioritize defense when low on health
    } else if (healthPercentage > 0.7 && state.playerHealth < 50) {
      return STRATEGIES.aggressive; // Be aggressive when winning
    }
    return STRATEGIES.balanced; // Default to balanced strategy
  }

  private analyzeSituation(state: BattleState) {
    return {
      aiHealthCritical: state.aiHealth < 30,
      playerHealthCritical: state.playerHealth < 30,
      aiHasAdvantage: state.aiHealth > state.playerHealth + 20,
      specialAvailable: state.specialAttacksUsed.ai < 2,
      hasActiveShield: state.activeEffects.ai.some(effect => 
        effect.type === 'shield' && effect.duration > 0
      )
    };
  }

  private chooseMoveType(
    strategy: typeof STRATEGIES[keyof typeof STRATEGIES],
    situation: ReturnType<AiStrategy['analyzeSituation']>,
    state: BattleState
  ): BattleMoveType {
    // Emergency healing
    if (situation.aiHealthCritical && !situation.hasActiveShield) {
      return 'defend';
    }

    // Use special attack in advantageous situations
    if (situation.specialAvailable && 
        (situation.aiHasAdvantage || situation.playerHealthCritical)) {
      const useSpecial = Math.random() < 0.7;
      if (useSpecial) return 'special';
    }

    // Choose move based on strategy probabilities
    const random = Math.random();
    let cumulativeProbability = 0;

    for (const [move, probability] of Object.entries(strategy)) {
      cumulativeProbability += probability;
      if (random <= cumulativeProbability) {
        return move as BattleMoveType;
      }
    }

    return 'attack'; // Fallback
  }

  private calculatePower(moveType: BattleMoveType): number {
    switch (moveType) {
      case 'attack':
        return 10 + getRandomInt(-2, 2);
      case 'defend':
        return 5 + getRandomInt(-1, 1);
      case 'special':
        return 15 + getRandomInt(-3, 3);
      default:
        return 10;
    }
  }
}

export const aiStrategy = new AiStrategy();